import { useEffect, useState } from 'react';
import { api, ApiFailure } from '../lib/api';
import { money } from '../lib/format';

export interface CodPendingGroup {
  driver: { id: string; name: string; driverNo: string };
  orders: Array<{
    id: string;
    orderNo: string;
    receiverName: string;
    amount: string;
    handedOverAt: string | null;
  }>;
  count: number;
  total: string;
  currency: string;
}

export interface CodPendingState {
  due: boolean;
  groups: CodPendingGroup[];
  count: number;
  truncated: boolean;
}

/**
 * 司机说交上来了、后台还没确认的现金。
 *
 * ★ 用户要的:「管理端那边打开页面的时候就会提示 xxx 司机给了哪些订单的多少钱,
 *   是否确认」,两个按钮:「下次再说」和「确认给了」。
 *
 * ★ 按**司机**一组一组地确认,不是一个「全部确认」的大按钮。
 *
 *   后台真正在做的动作是「从一个人手里数一沓钱」—— 一个人一沓。
 *   混在一起点一次的话,三个司机里有一个少给了,账上也已经全认下来了,
 *   而事后查不出是谁那一沓不对。
 *
 * ★ 「下次再说」什么都不往服务端写 —— 它的意思正是「我还没数完,下次再问我」。
 *   给它记一个「已推迟」的状态,这笔钱就从待确认清单里消失了,
 *   而没有人会再提起它。
 */
export function CodConfirmDialog({
  state,
  onLater,
  onDone,
}: {
  state: CodPendingState;
  onLater: () => void;
  onDone: () => void;
}) {
  /*
   * ★ 弹窗开着的时候把**背景**的滚动锁掉。
   *
   *   用户报的:「有时候在这里滑动是底下的页面在滚动,这个弹窗没有滚动,
   *   导致没办法滑到底下点下次再说」。
   *
   *   手指落在弹窗里不可滚动的地方(标题、某一组的空白处)时,
   *   滚动会**链**到后面的文档上 —— 于是底下那一页在动,而他要点的按钮
   *   还在屏幕外。锁掉 body 之后,滚动只能落在弹窗自己身上。
   */
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  /** 已经确认掉的那几个司机 —— 一次弹窗里可以一个个点完 */
  const [done, setDone] = useState<Set<string>>(new Set());

  const left = state.groups.filter((g) => !done.has(g.driver.id));

  async function confirm(g: CodPendingGroup): Promise<void> {
    setBusy(g.driver.id);
    setErr(null);
    try {
      await api('/settlements/cod-handovers/confirm', {
        method: 'POST',
        body: { orderIds: g.orders.map((o) => o.id) },
      });
      const next = new Set(done);
      next.add(g.driver.id);
      setDone(next);
      // 最后一个也点完了就关掉 —— 不用他再去点一次「下次再说」
      if (next.size === state.groups.length) onDone();
    } catch (e) {
      setErr(e instanceof ApiFailure ? e.message : '确认失败');
    } finally {
      setBusy(null);
    }
  }

  return (
    /*
      ★ z 要高于详情抽屉(z-50):这是钱,不是一条可以待会儿再看的通知。

      ★ **外层**负责滚动(overflow-y-auto + overscroll-contain),
        而不是只让里面那个白盒子滚:手指落在盒子外的遮罩上、或者盒子里
        任何不可滚的地方,滚的都该是这个弹窗。
        overscroll-contain 再挡住「滚到头之后链给后面那一页」。

      ★ items-start + py-6 而不是 place-items-center:居中的盒子一旦比屏幕高,
        它的上半截会被顶出可视区,而那时候连往上滚都滚不回去。
    */
    <div
      className="fixed inset-0 z-[80] flex items-start justify-center overflow-y-auto
                 overscroll-contain bg-black/50 px-4 py-6"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="确认收到现金"
        className="w-full max-w-lg rounded-2xl bg-white p-4 shadow-xl dark:bg-slate-800"
      >
        <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
          司机交上来的现金,确认收到了吗
        </h3>
        <p className="mt-1 text-xs text-slate-500">
          下面是司机在手机上点过「已经给了」的单。数完钱再点确认 —— 确认之后这笔就不再提示了
        </p>

        {err && (
          <div className="mt-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{err}</div>
        )}

        {state.truncated && (
          <div className="mt-2 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">
            待确认的单太多,这次只列了前 500 单 —— 先确认掉一批再看一次
          </div>
        )}

        <div className="mt-3 space-y-3">
          {left.map((g) => (
            <div
              key={g.driver.id}
              aria-label={`待确认 ${g.driver.name}`}
              className="rounded-xl border border-slate-200 p-3 dark:border-slate-700"
            >
              <div className="flex items-baseline justify-between gap-3">
                <div className="min-w-0">
                  <div className="truncate font-medium text-slate-900 dark:text-slate-100">
                    {g.driver.name}
                    <span className="ml-2 text-xs text-slate-400">{g.driver.driverNo}</span>
                  </div>
                  <div className="text-xs text-slate-500">{g.count} 单</div>
                </div>
                {/*
                  ★ 合计用大字:后台要做的第一件事是数钱,那一步只需要一个数字。
                */}
                <div className="shrink-0 text-xl font-semibold tabular-nums text-slate-900 dark:text-slate-100">
                  {money(g.total, g.currency)}
                </div>
              </div>

              {/*
                ★ 单号和每单金额也列出来 —— 数不对的时候要能一单一单核。
                  只给一个合计的话,差了一单他没有任何线索。
              */}
              {/*
                ★ 这一段**不再自己滚**(原来是 max-h-40 overflow-auto)。
                  嵌套的滚动区是个陷阱:手指落在它上面、而它已经到底时,
                  这一下要么被它吃掉、要么链到最外面那一页 ——
                  而人以为自己在滑弹窗。整个弹窗作为一个东西滚就够了。
              */}
              <ul className="mt-2 space-y-1 text-xs">
                {g.orders.map((o) => (
                  <li
                    key={o.id}
                    className="flex justify-between gap-3 text-slate-600 dark:text-slate-300"
                  >
                    {/*
                      ★ 名字在前、单号在后。
                        用户原话:「只有订单号,没有收件人的名字」——
                        对账时人认的是「送给王小明那一单」,
                        而单号要回列表里搜一遍才知道是哪一单。
                    */}
                    <span className="min-w-0 truncate">
                      {o.receiverName}
                      <span className="ml-2 font-mono text-slate-400">{o.orderNo}</span>
                    </span>
                    <span className="shrink-0 tabular-nums">{money(o.amount, g.currency)}</span>
                  </li>
                ))}
              </ul>

              <button
                onClick={() => void confirm(g)}
                disabled={busy !== null}
                className="mt-2 h-9 w-full rounded-lg bg-brand-600 text-sm font-medium text-white
                           disabled:opacity-40"
              >
                {busy === g.driver.id ? '记着…' : `确认收到 ${money(g.total, g.currency)}`}
              </button>
            </div>
          ))}
        </div>

        {/*
          ★ 「下次再说」**吸在弹窗底部**,不用滑到最后才点得到。
            用户原话:「导致没办法滑到底下点下次再说」——
            待确认的司机一多,这个按钮就被推到屏幕外面去了,
            而它恰恰是「我还没数完」时唯一的出路。
        */}
        <div
          className="sticky bottom-0 -mx-4 -mb-4 mt-4 border-t border-slate-100 bg-white px-4 py-3
                     dark:border-slate-700 dark:bg-slate-800"
        >
          <button
            onClick={onLater}
            className="h-10 w-full rounded-lg border border-slate-300 text-sm text-slate-700
                       dark:border-slate-600 dark:text-slate-300"
          >
            下次再说
          </button>
        </div>
      </div>
    </div>
  );
}
